import {
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { useCallback, useEffect, useState } from 'react';
import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { useDidUpdate } from '@mantine/hooks';
import { CapChartLocalStorageSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/chart.utils.ts';
import { CapTableRail } from '@dealstack/shared/lib/generated';

// CAP-1972: the title rail is persisted alongside the chart settings but is not itself a chart
// rendering setting, so extend the stored shape here rather than widening CapChartLocalStorageSettings.
type CapChartStoredSettings = CapChartLocalStorageSettings & { rail?: CapTableRail };

export const useCapChartPageHeaderController = (dealId: string) => {
	const [showSummarized, setShowSummarized] = useState(true);
	const [valuesHeldType, setValuesHeldType] = useState(CAP_TABLE_VALUES_HELD_TYPE.SECURITIES);
	const [valueDisplayMode, setValueDisplayMode] = useState(CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE);
	const [inSecuritiesMode, setInSecuritiesMode] = useState(false);
	const [showSecuritiesDrawer, setShowSecuritiesDrawer] = useState(false);
	// CAP-1972: beneficial is the default rail; the toggle is only shown on nominee-structure deals.
	const [rail, setRail] = useState(CapTableRail.BENEFICIAL);

	useEffect(() => {
		const settings = StorageUtilities.getItem<CapChartStoredSettings>(
			StorageKeys.CAP_CHART_LOCAL_STORAGE_SETTINGS_KEY,
			dealId
		);
		if (!settings) {
			return;
		}

		setShowSummarized(settings.showSummarized);
		setValuesHeldType(settings.valuesHeldType);
		setValueDisplayMode(settings.valueDisplayMode);
		// Older persisted settings predate the rail — fall back to the beneficial default.
		setRail(settings.rail ?? CapTableRail.BENEFICIAL);
	}, []);

	useDidUpdate(() => {
		const settings: CapChartStoredSettings = {
			valueDisplayMode,
			showSummarized,
			valuesHeldType,
			rail,
		};
		StorageUtilities.setItem(
			StorageKeys.CAP_CHART_LOCAL_STORAGE_SETTINGS_KEY,
			settings,
			dealId
		);
	}, [valueDisplayMode, showSummarized, valuesHeldType, rail]);

	const onSummarizedChange = useCallback((value: boolean) => {
		setShowSummarized(value);
	}, []);

	const onValuesHeldTypeChange = useCallback((value: CAP_TABLE_VALUES_HELD_TYPE) => {
		setValuesHeldType(value);
	}, []);

	const onValueDisplayModeChange = useCallback((value: CAP_TABLE_VALUE_DISPLAY_MODE) => {
		setValueDisplayMode?.(value);
	}, []);

	const onToggleSpecifySecurities = useCallback((value: boolean) => {
		setInSecuritiesMode(() => {
			setShowSecuritiesDrawer(value);
			return value;
		});
	}, []);

	const onToggleSpecifySecuritiesDrawer = useCallback(() => {
		setShowSecuritiesDrawer((prevValue) => !prevValue);
	}, []);

	return {
		valueDisplayMode,
		valuesHeldType,
		showSummarized,
		onSummarizedChange,
		onValueDisplayModeChange,
		onValuesHeldTypeChange,
		onToggleSpecifySecurities,
		onToggleSpecifySecuritiesDrawer,
		showSecuritiesDrawer,
		inSecuritiesMode,
		setShowSecuritiesDrawer,
		rail,
		setRail,
	};
};
